// ============================================================================
// InventorySyncTab — vendor inventory sync dashboard
//
// Consumes the DRF viewsets in ai_agents/vendor_agent/api/views.py:
//   GET    /vendor-inventory/sources/
//   POST   /vendor-inventory/sources/{id}/sync/
//   GET    /vendor-inventory/sources/{id}/health/
//   GET    /vendor-inventory/sources/{id}/runs/
//   GET    /vendor-inventory/runs/{id}/
//   GET    /vendor-inventory/runs/{id}/diff/?category=...
//
// Layout
//  ┌─ Stat tiles (totals across visible sources)
//  ├─ Sources table (one row per VendorInventorySource)
//  │     click row → opens RUNS drawer (paginated history for that source)
//  │                 click a run inside it → opens RUN DETAIL drawer
//  │                 (counts + per-category diff drill-down)
// ============================================================================
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  DatabaseOutlined,
  ExclamationCircleOutlined,
  FileSearchOutlined,
  InboxOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  SyncOutlined,
  ThunderboltOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import {
  Button,
  Drawer,
  Input,
  message,
  Pagination,
  Segmented,
  Select,
  Spin,
  Table,
  Tag,
  Tooltip,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  acceptUnmappedMapping,
  getUnmappedMatches,
  getVendorInventoryRun,
  getVendorInventoryRunDiff,
  getVendorInventorySourceHealth,
  getVendorInventorySourceRuns,
  getVendorInventorySources,
  triggerVendorInventorySync,
} from "../../apis/vendor_inventory_dashboard.api";
import {
  IMatchCandidate,
  IVendorInventoryDiffCategory,
  IVendorInventoryDiffResponse,
  IVendorInventoryHealthWindow,
  IVendorInventorySource,
  IVendorInventorySourceHealth,
  IVendorInventorySyncRunDetail,
  IVendorInventorySyncRunSummary,
  IVendorInventorySyncStatus,
} from "../../interfaces/vendor_inventory_dashboard.interface";
import styles from "./inventory_sync_tab.module.scss";

// ──────────────── helpers ────────────────

const formatRelativeTime = (iso: string | null | undefined) => {
  if (!iso) return "—";
  const d = dayjs(iso);
  const now = dayjs();
  const diffMin = now.diff(d, "minute");
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = now.diff(d, "hour");
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = now.diff(d, "day");
  if (diffDay < 7) return `${diffDay}d ago`;
  return d.format("DD MMM");
};

const formatDuration = (ms: number | null | undefined) => {
  if (ms == null) return "—";
  if (ms < 1000) return `${ms}ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  const rem = Math.round(s - m * 60);
  return `${m}m ${rem}s`;
};

const formatPercent = (v: number | null | undefined) => {
  if (v == null) return "—";
  return `${(v * 100).toFixed(0)}%`;
};

const STATUS_LABELS: Record<IVendorInventorySyncStatus, string> = {
  running: "Running",
  success: "Success",
  partial: "Partial",
  failed: "Failed",
};

const statusBadgeClass = (s: IVendorInventorySyncStatus | null | undefined) => {
  if (s === "success") return styles.statusSuccess;
  if (s === "partial") return styles.statusPartial;
  if (s === "failed") return styles.statusFailed;
  if (s === "running") return styles.statusRunning;
  return styles.statusNeutral;
};

const StatusBadge: React.FC<{
  status: IVendorInventorySyncStatus | null | undefined;
}> = ({ status }) => {
  if (!status) {
    return (
      <span className={`${styles.statusBadge} ${styles.statusNeutral}`}>
        <ClockCircleOutlined /> Pending
      </span>
    );
  }
  const icon =
    status === "success" ? (
      <CheckCircleOutlined />
    ) : status === "partial" ? (
      <WarningOutlined />
    ) : status === "failed" ? (
      <CloseCircleOutlined />
    ) : (
      <SyncOutlined spin />
    );
  return (
    <span className={`${styles.statusBadge} ${statusBadgeClass(status)}`}>
      {icon} {STATUS_LABELS[status]}
    </span>
  );
};

// ──────────────── stat tiles ────────────────

interface IAggStats {
  totalSources: number;
  enabledSources: number;
  successfulLast: number;
  partialLast: number;
  failedLast: number;
  totalRecordsNew: number;
  totalRecordsUpdated: number;
}

const computeAggStats = (sources: IVendorInventorySource[]): IAggStats => {
  let enabledSources = 0;
  let successfulLast = 0;
  let partialLast = 0;
  let failedLast = 0;
  for (const s of sources) {
    if (s.is_enabled) enabledSources++;
    if (s.last_run_status === "success") successfulLast++;
    else if (s.last_run_status === "partial") partialLast++;
    else if (s.last_run_status === "failed") failedLast++;
  }
  return {
    totalSources: sources.length,
    enabledSources,
    successfulLast,
    partialLast,
    failedLast,
    totalRecordsNew: 0,
    totalRecordsUpdated: 0,
  };
};

const StatsTiles: React.FC<{ stats: IAggStats; loading: boolean }> = ({
  stats,
  loading,
}) => {
  const tiles = [
    {
      label: "Total Sources",
      value: stats.totalSources,
      hint: `${stats.enabledSources} enabled`,
      icon: <DatabaseOutlined />,
      iconClass: styles.statIconPrimary,
    },
    {
      label: "Healthy",
      value: stats.successfulLast,
      hint: "last run = success",
      icon: <CheckCircleOutlined />,
      iconClass: styles.statIconSuccess,
    },
    {
      label: "Partial",
      value: stats.partialLast,
      hint: "needs attention",
      icon: <WarningOutlined />,
      iconClass: styles.statIconWarning,
    },
    {
      label: "Failed",
      value: stats.failedLast,
      hint: "last run = failed",
      icon: <CloseCircleOutlined />,
      iconClass: styles.statIconDanger,
    },
    {
      label: "Auto Sync",
      value: stats.enabledSources,
      hint: "running on cron",
      icon: <ThunderboltOutlined />,
      iconClass: styles.statIconInfo,
    },
  ];
  return (
    <div className={styles.statsGrid}>
      {tiles.map((t) => (
        <div className={styles.statCard} key={t.label}>
          <div className={styles.statCardHead}>
            <div className={styles.statLabel}>{t.label}</div>
            <div className={`${styles.statIcon} ${t.iconClass}`}>{t.icon}</div>
          </div>
          <div className={styles.statValue}>{loading ? "…" : t.value}</div>
          <div className={styles.statHint}>{t.hint}</div>
        </div>
      ))}
    </div>
  );
};

// ──────────────── runs drawer (per-source history) ────────────────

const RunsDrawer: React.FC<{
  open: boolean;
  source: IVendorInventorySource | null;
  onClose: () => void;
  onOpenRun: (runId: number) => void;
}> = ({ open, source, onClose, onOpenRun }) => {
  const [runs, setRuns] = useState<IVendorInventorySyncRunSummary[]>([]);
  const [health, setHealth] = useState<IVendorInventorySourceHealth | null>(
    null
  );
  const [healthWindow, setHealthWindow] =
    useState<IVendorInventoryHealthWindow>("today");
  const [loading, setLoading] = useState(false);
  const [healthLoading, setHealthLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 20;

  // Load runs (paginated) — only depends on source + page
  const loadRuns = useCallback(async () => {
    if (!source) return;
    setLoading(true);
    const runResp = await getVendorInventorySourceRuns(source.id, {
      page,
      page_size: PAGE_SIZE,
    });
    if (runResp) {
      setRuns(runResp.results);
      setTotal(runResp.count);
    }
    setLoading(false);
  }, [source, page]);

  // Load health — depends on source + healthWindow
  const loadHealth = useCallback(async () => {
    if (!source) return;
    setHealthLoading(true);
    const healthResp = await getVendorInventorySourceHealth(
      source.id,
      healthWindow
    );
    if (healthResp) setHealth(healthResp);
    setHealthLoading(false);
  }, [source, healthWindow]);

  useEffect(() => {
    if (open) loadRuns();
    else {
      setRuns([]);
      setHealth(null);
      setPage(1);
      setTotal(0);
      setHealthWindow("today");
    }
  }, [open, loadRuns]);

  useEffect(() => {
    if (open) loadHealth();
  }, [open, loadHealth]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={720}
      destroyOnClose
      title={
        source ? (
          <div className={styles.drawerHead}>
            <div className={styles.drawerTitle}>
              {source.name || `Source #${source.id}`}
            </div>
            <div className={styles.drawerSubtitle}>
              {source.vendor_name} · {source.source_type} ·{" "}
              {source.is_enabled ? "Enabled" : "Disabled"} · every{" "}
              {source.sync_frequency_minutes}m
            </div>
          </div>
        ) : (
          "Loading…"
        )
      }
    >
      {loading && runs.length === 0 ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
          <Spin />
        </div>
      ) : (
        <>
          {/* ===== Health rollup (today / 7d toggle) ===== */}
          <div
            className={styles.drawerSectionTitle}
            style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
          >
            <span>
              <FileSearchOutlined />{" "}
              {healthWindow === "today" ? "Today" : "Last 7 days"}
            </span>
            <Segmented
              size="small"
              value={healthWindow}
              onChange={(v) =>
                setHealthWindow(v as IVendorInventoryHealthWindow)
              }
              options={[
                { label: "Today", value: "today" },
                { label: "7 Days", value: "7d" },
              ]}
            />
          </div>
          {healthLoading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 30 }}>
              <Spin size="small" />
            </div>
          ) : health ? (
            <div className={styles.runStatGrid}>
              <div className={styles.runStatTile}>
                <div className={styles.runStatLabel}>Total Runs</div>
                <div className={styles.runStatValue}>{health.total_runs}</div>
              </div>
              <div className={styles.runStatTile}>
                <div className={styles.runStatLabel}>Success Rate</div>
                <div className={styles.runStatValue}>
                  {formatPercent(health.success_rate)}
                </div>
                <div className={styles.runStatHint}>
                  {health.success_runs} / {health.total_runs} succeeded
                </div>
              </div>
              <div className={styles.runStatTile}>
                <div className={styles.runStatLabel}>Avg Duration</div>
                <div className={styles.runStatValue}>
                  {formatDuration(health.avg_duration_ms)}
                </div>
              </div>
              <div className={styles.runStatTile}>
                <div className={styles.runStatLabel}>Records Synced</div>
                <div className={styles.runStatValue}>
                  {health.total_records_synced}
                </div>
                <div className={styles.runStatHint}>created + updated</div>
              </div>
              <div className={styles.runStatTile}>
                <div className={styles.runStatLabel}>Records Removed</div>
                <div className={styles.runStatValue}>
                  {health.total_records_removed}
                </div>
              </div>
              <div className={styles.runStatTile}>
                <div className={styles.runStatLabel}>Unmapped SKUs</div>
                <div className={styles.runStatValue}>
                  {health.total_unmapped_skus}
                </div>
              </div>
            </div>
          ) : (
            <div className={styles.diffEmpty}>
              No runs in this window.
            </div>
          )}

          {/* ===== Run history ===== */}
          <div className={styles.drawerSectionTitle}>
            <ClockCircleOutlined /> Run history ({total})
          </div>
          {runs.length === 0 ? (
            <div className={styles.diffEmpty}>
              No runs recorded yet for this source.
            </div>
          ) : (
            runs.map((run) => (
              <div
                key={run.id}
                className={styles.runCard}
                onClick={() => onOpenRun(run.id)}
              >
                <div className={styles.runCardHead}>
                  <div>
                    <div className={styles.runCardTime}>
                      {dayjs(run.started_at).format("DD MMM, hh:mm A")}
                    </div>
                    <div className={styles.runCardMeta}>
                      Run #{run.id} · {run.triggered_by} ·{" "}
                      {formatDuration(run.duration_ms)}
                    </div>
                  </div>
                  <StatusBadge status={run.status} />
                </div>
                <div className={styles.countsRow}>
                  <span className={`${styles.countPill} ${styles.countNew}`}>
                    <span className={styles.countLabel}>New</span>{" "}
                    {run.records_new}
                  </span>
                  <span
                    className={`${styles.countPill} ${styles.countUpdated}`}
                  >
                    <span className={styles.countLabel}>Updated</span>{" "}
                    {run.records_updated}
                  </span>
                  <span className={styles.countPill}>
                    <span className={styles.countLabel}>Unchanged</span>{" "}
                    {run.records_unchanged}
                  </span>
                  <span
                    className={`${styles.countPill} ${styles.countRemoved}`}
                  >
                    <span className={styles.countLabel}>Removed</span>{" "}
                    {run.records_removed}
                  </span>
                  {run.rows_failed > 0 && (
                    <span
                      className={`${styles.countPill} ${styles.countFailed}`}
                    >
                      <span className={styles.countLabel}>Failed</span>{" "}
                      {run.rows_failed}
                    </span>
                  )}
                  {run.unmapped_skus_count > 0 && (
                    <span
                      className={`${styles.countPill} ${styles.countFailed}`}
                    >
                      <span className={styles.countLabel}>Unmapped</span>{" "}
                      {run.unmapped_skus_count}
                    </span>
                  )}
                </div>
              </div>
            ))
          )}
          {total > PAGE_SIZE && (
            <div className={styles.diffPagination}>
              <Pagination
                current={page}
                total={total}
                pageSize={PAGE_SIZE}
                onChange={(p) => setPage(p)}
                showSizeChanger={false}
                size="small"
              />
            </div>
          )}
        </>
      )}
    </Drawer>
  );
};

// ──────────────── run-detail drawer ────────────────

const DIFF_CATEGORIES: {
  key: IVendorInventoryDiffCategory;
  label: string;
}[] = [
  { key: "new", label: "New" },
  { key: "changed", label: "Updated" },
  { key: "removed", label: "Removed" },
  { key: "unmapped", label: "Unmapped" },
  { key: "errors", label: "Errors" },
];

const renderDiffItem = (
  category: IVendorInventoryDiffCategory,
  item: any,
  idx: number
): React.ReactNode => {
  if (category === "unmapped") {
    return (
      <div key={idx} className={styles.diffItem}>
        <strong>{String(item)}</strong>
        <div className={styles.diffItemMeta}>
          No matching VendorVariant.vendor_product_name
        </div>
      </div>
    );
  }
  if (category === "errors") {
    return (
      <div key={idx} className={styles.diffItem}>
        <div className={styles.diffItemHead}>
          <strong>{item.stock_item || "(unknown)"}</strong>
          {item.raw_row_index != null && (
            <span className={styles.diffItemMeta}>row #{item.raw_row_index}</span>
          )}
        </div>
        <div className={styles.diffItemMeta}>{item.error}</div>
      </div>
    );
  }
  if (category === "new") {
    return (
      <div key={idx} className={styles.diffItem}>
        <div className={styles.diffItemHead}>
          <strong>{item.stock_item}</strong>
          <span className={styles.diffItemMeta}>qty {item.qty}</span>
        </div>
        <div className={styles.diffItemMeta}>
          variant #{item.variant_id}
          {item.batch ? ` · batch ${item.batch}` : ""}
          {item.godown ? ` · ${item.godown}` : ""}
        </div>
      </div>
    );
  }
  if (category === "changed") {
    const delta = item.delta ?? item.qty_after - item.qty_before;
    return (
      <div key={idx} className={styles.diffItem}>
        <div className={styles.diffItemHead}>
          <strong>{item.stock_item}</strong>
          <span className={styles.diffItemMeta}>
            {item.qty_before} → {item.qty_after}{" "}
            <Tag
              color={delta > 0 ? "green" : delta < 0 ? "red" : "default"}
              style={{ marginLeft: 4 }}
            >
              {delta > 0 ? "+" : ""}
              {delta}
            </Tag>
          </span>
        </div>
        <div className={styles.diffItemMeta}>
          variant #{item.variant_id}
          {item.batch ? ` · batch ${item.batch}` : ""}
        </div>
      </div>
    );
  }
  if (category === "removed") {
    return (
      <div key={idx} className={styles.diffItem}>
        <div className={styles.diffItemHead}>
          <strong>{item.stock_item || `inv #${item.inventory_id}`}</strong>
          <span className={styles.diffItemMeta}>was {item.last_qty}</span>
        </div>
        <div className={styles.diffItemMeta}>
          variant #{item.variant_id}
          {item.batch ? ` · batch ${item.batch}` : ""}
          {item.godown ? ` · ${item.godown}` : ""}
          {item.last_seen_at &&
            ` · last seen ${dayjs(item.last_seen_at).format("DD MMM, hh:mm A")}`}
        </div>
      </div>
    );
  }
  return null;
};

const RunDetailDrawer: React.FC<{
  open: boolean;
  runId: number | null;
  onClose: () => void;
}> = ({ open, runId, onClose }) => {
  const [run, setRun] = useState<IVendorInventorySyncRunDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [category, setCategory] =
    useState<IVendorInventoryDiffCategory>("new");
  const [diff, setDiff] = useState<IVendorInventoryDiffResponse | null>(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [diffPage, setDiffPage] = useState(1);
  const PAGE_SIZE = 50;

  // Unmapped matching state
  const [matchResults, setMatchResults] = useState<Record<string, IMatchCandidate[]> | null>(null);
  const [matchLoading, setMatchLoading] = useState(false);
  const [acceptedMappings, setAcceptedMappings] = useState<Set<string>>(new Set());
  const [acceptingKey, setAcceptingKey] = useState<string | null>(null);

  const handleFindMatches = async () => {
    if (!runId) return;
    setMatchLoading(true);
    const resp = await getUnmappedMatches(runId);
    if (resp) setMatchResults(resp.results);
    setMatchLoading(false);
  };

  const handleAcceptMapping = async (vendorProductName: string, vendorVariantId: number, variantId: number) => {
    if (!runId) return;
    const key = `${vendorProductName}:${vendorVariantId}`;
    setAcceptingKey(key);
    const resp = await acceptUnmappedMapping(runId, {
      vendor_product_name: vendorProductName,
      vendor_variant_id: vendorVariantId,
    });
    if (resp) {
      message.success(`Mapped "${vendorProductName}" → variant #${variantId}`);
      setAcceptedMappings((prev) => new Set(prev).add(vendorProductName));
      // Remove accepted SKU from match results so it disappears
      setMatchResults((prev) => {
        if (!prev) return prev;
        const updated = { ...prev };
        delete updated[vendorProductName];
        return updated;
      });
    } else {
      message.error(`Failed to map "${vendorProductName}" — check console for details`);
    }
    setAcceptingKey(null);
  };

  // Load run detail
  useEffect(() => {
    if (!open || !runId) return;
    setLoading(true);
    setRun(null);
    getVendorInventoryRun(runId).then((data) => {
      if (data) setRun(data);
      setLoading(false);
    });
    setCategory("new");
    setDiffPage(1);
    setDiff(null);
    setMatchResults(null);
    setAcceptedMappings(new Set());
  }, [open, runId]);

  // Load diff slice when category or page changes
  useEffect(() => {
    if (!open || !runId) return;
    setDiffLoading(true);
    getVendorInventoryRunDiff(runId, category, diffPage, PAGE_SIZE).then(
      (data) => {
        if (data) setDiff(data);
        setDiffLoading(false);
      }
    );
  }, [open, runId, category, diffPage]);

  const onChangeCategory = (key: IVendorInventoryDiffCategory) => {
    setCategory(key);
    setDiffPage(1);
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={760}
      destroyOnClose
      title={
        run ? (
          <div className={styles.drawerHead}>
            <div className={styles.drawerTitle}>
              Run #{run.id} · <StatusBadge status={run.status} />
            </div>
            <div className={styles.drawerSubtitle}>
              {run.source_name} ({run.vendor_name}) ·{" "}
              {dayjs(run.started_at).format("DD MMM YYYY, hh:mm A")} ·{" "}
              {formatDuration(run.duration_ms)} · {run.triggered_by}
            </div>
          </div>
        ) : (
          "Loading…"
        )
      }
    >
      {loading || !run ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
          <Spin />
        </div>
      ) : (
        <>
          {/* ===== Stat tiles ===== */}
          <div className={styles.drawerSectionTitle}>
            <DatabaseOutlined /> Counts
          </div>
          <div className={styles.runStatGrid}>
            <div className={styles.runStatTile}>
              <div className={styles.runStatLabel}>Rows Read</div>
              <div className={styles.runStatValue}>{run.rows_read}</div>
              <div className={styles.runStatHint}>from sheet</div>
            </div>
            <div className={styles.runStatTile}>
              <div className={styles.runStatLabel}>Rows Skipped</div>
              <div className={styles.runStatValue}>{run.rows_skipped}</div>
              <div className={styles.runStatHint}>by skip_rules</div>
            </div>
            <div className={styles.runStatTile}>
              <div className={styles.runStatLabel}>Rows Failed</div>
              <div className={styles.runStatValue}>{run.rows_failed}</div>
              <div className={styles.runStatHint}>validation / mapping</div>
            </div>
            <div className={styles.runStatTile}>
              <div className={styles.runStatLabel}>New</div>
              <div className={styles.runStatValue}>{run.records_new}</div>
              <div className={styles.runStatHint}>created in DB</div>
            </div>
            <div className={styles.runStatTile}>
              <div className={styles.runStatLabel}>Updated</div>
              <div className={styles.runStatValue}>{run.records_updated}</div>
              <div className={styles.runStatHint}>qty changed</div>
            </div>
            <div className={styles.runStatTile}>
              <div className={styles.runStatLabel}>Unchanged</div>
              <div className={styles.runStatValue}>
                {run.records_unchanged}
              </div>
              <div className={styles.runStatHint}>qty matched</div>
            </div>
            <div className={styles.runStatTile}>
              <div className={styles.runStatLabel}>Removed</div>
              <div className={styles.runStatValue}>{run.records_removed}</div>
              <div className={styles.runStatHint}>zeroed by sweep</div>
            </div>
            <div className={styles.runStatTile}>
              <div className={styles.runStatLabel}>Unmapped SKUs</div>
              <div className={styles.runStatValue}>
                {run.unmapped_skus_count}
              </div>
              <div className={styles.runStatHint}>no VendorVariant</div>
            </div>
            <div className={styles.runStatTile}>
              <div className={styles.runStatLabel}>Total Qty Δ</div>
              <div className={styles.runStatValue}>
                {run.total_qty_after != null && run.total_qty_before != null
                  ? Number(run.total_qty_after) - Number(run.total_qty_before)
                  : "—"}
              </div>
              <div className={styles.runStatHint}>
                {run.total_qty_before ?? "—"} → {run.total_qty_after ?? "—"}
              </div>
            </div>
          </div>

          {/* ===== Notification badges ===== */}
          {run.notification_sent && (
            <>
              <div className={styles.drawerSectionTitle}>
                <ExclamationCircleOutlined /> Ops Notification Sent
              </div>
              <div className={styles.notificationBadgeRow}>
                {(run.notification_reasons || []).map((r) => (
                  <Tag color="orange" key={r}>
                    {r.replace(/_/g, " ")}
                  </Tag>
                ))}
              </div>
            </>
          )}

          {/* ===== Fatal error block ===== */}
          {run.fatal_error && (
            <>
              <div className={styles.drawerSectionTitle}>
                <CloseCircleOutlined /> Fatal Error
              </div>
              <div className={styles.fatalErrorBox}>{run.fatal_error}</div>
            </>
          )}

          {/* ===== Diff drill-down ===== */}
          <div className={styles.drawerSectionTitle}>
            <FileSearchOutlined /> Diff
          </div>
          <Segmented
            value={category}
            onChange={(v) =>
              onChangeCategory(v as IVendorInventoryDiffCategory)
            }
            options={DIFF_CATEGORIES.map((c) => ({
              label: c.label,
              value: c.key,
            }))}
            block
            style={{ marginBottom: 12 }}
          />

          {run.diff_truncated && (
            <div className={styles.diffTruncatedNote}>
              Diff list was capped — counts above are exact, but only the first
              1000 items per category are stored on the run.
            </div>
          )}

          {category === "unmapped" && diff && diff.results.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <Button
                type="primary"
                icon={<FileSearchOutlined />}
                loading={matchLoading}
                onClick={handleFindMatches}
              >
                {matchResults ? "Re-run Matching" : "Find Matches"}
              </Button>
              {matchResults && (
                <span style={{ marginLeft: 12, fontSize: 12, color: "#64748b" }}>
                  {acceptedMappings.size} mapped
                </span>
              )}
            </div>
          )}

          {diffLoading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 30 }}>
              <Spin size="small" />
            </div>
          ) : !diff || diff.results.length === 0 ? (
            <div className={styles.diffEmpty}>No items in this category.</div>
          ) : (
            <>
              <div className={styles.diffList}>
                {diff.results.map((item, idx) => {
                  if (category === "unmapped" && matchResults) {
                    const sku = String(item);
                    const candidates = matchResults[sku] || [];
                    const isAccepted = acceptedMappings.has(sku);
                    return (
                      <div key={idx} className={styles.diffItem}>
                        <div className={styles.diffItemHead}>
                          <strong>{sku}</strong>
                          {isAccepted && (
                            <Tag color="green" style={{ marginLeft: 8 }}>Mapped</Tag>
                          )}
                        </div>
                        {!isAccepted && candidates.length === 0 && (
                          <div className={styles.diffItemMeta}>No close matches found</div>
                        )}
                        {!isAccepted && candidates.length > 0 && (
                          <div className={styles.matchCandidates}>
                            {candidates.map((c, ci) => (
                              <div key={ci} className={styles.matchCandidate}>
                                <div className={styles.matchCandidateInfo}>
                                  <span className={styles.matchCandidateName}>
                                    {c.private_label_product_name}
                                  </span>
                                  <span className={styles.matchCandidateSku}>
                                    {c.sku || "—"}
                                  </span>
                                  <Tag
                                    color={
                                      c.confidence_tier === "auto_approve" ? "green" :
                                      c.confidence_tier === "strong_suggestion" ? "blue" :
                                      c.confidence_tier === "review_needed" ? "orange" : "red"
                                    }
                                  >
                                    {c.score}%
                                  </Tag>
                                </div>
                                <Button
                                  size="small"
                                  type="link"
                                  loading={acceptingKey === `${sku}:${c.vendor_variant_id}`}
                                  onClick={() => handleAcceptMapping(sku, c.vendor_variant_id, c.variant_id)}
                                >
                                  Accept
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  }
                  return renderDiffItem(category, item, idx);
                })}
              </div>
              {diff.total > PAGE_SIZE && (
                <div className={styles.diffPagination}>
                  <Pagination
                    current={diffPage}
                    total={diff.total}
                    pageSize={PAGE_SIZE}
                    onChange={(p) => setDiffPage(p)}
                    showSizeChanger={false}
                    size="small"
                  />
                </div>
              )}
            </>
          )}
        </>
      )}
    </Drawer>
  );
};

// ────────────────── Main tab component ──────────────────

const InventorySyncTab: React.FC = () => {
  // Sources list
  const [sources, setSources] = useState<IVendorInventorySource[]>([]);
  const [sourcesLoading, setSourcesLoading] = useState(false);
  const [sourcesPage, setSourcesPage] = useState(1);
  const [sourcesTotal, setSourcesTotal] = useState(0);
  const PAGE_SIZE = 25;

  // Filters
  const [search, setSearch] = useState("");
  const [enabledFilter, setEnabledFilter] = useState<
    "all" | "enabled" | "disabled"
  >("all");

  // Drawers
  const [runsDrawerOpen, setRunsDrawerOpen] = useState(false);
  const [activeSource, setActiveSource] =
    useState<IVendorInventorySource | null>(null);

  const [runDrawerOpen, setRunDrawerOpen] = useState(false);
  const [activeRunId, setActiveRunId] = useState<number | null>(null);

  // Manual sync busy state
  const [syncingId, setSyncingId] = useState<number | null>(null);

  const loadSources = useCallback(async () => {
    setSourcesLoading(true);
    const isEnabled =
      enabledFilter === "all" ? undefined : enabledFilter === "enabled";
    const data = await getVendorInventorySources({
      page: sourcesPage,
      page_size: PAGE_SIZE,
      is_enabled: isEnabled,
    });
    if (data) {
      setSources(data.results);
      setSourcesTotal(data.count);
    }
    setSourcesLoading(false);
  }, [sourcesPage, enabledFilter]);

  useEffect(() => {
    loadSources();
  }, [loadSources]);

  // Search is client-side over the current page (the API doesn't have a search filter)
  const filteredSources = useMemo(() => {
    if (!search.trim()) return sources;
    const q = search.trim().toLowerCase();
    return sources.filter(
      (s) =>
        (s.name || "").toLowerCase().includes(q) ||
        (s.vendor_name || "").toLowerCase().includes(q)
    );
  }, [sources, search]);

  const stats = useMemo(() => computeAggStats(sources), [sources]);

  const handleOpenRuns = (source: IVendorInventorySource) => {
    setActiveSource(source);
    setRunsDrawerOpen(true);
  };

  const handleOpenRun = (runId: number) => {
    setActiveRunId(runId);
    setRunDrawerOpen(true);
  };

  const handleManualSync = async (
    e: React.MouseEvent,
    source: IVendorInventorySource
  ) => {
    e.stopPropagation();
    if (!source.is_enabled) {
      message.warning("Enable the source before syncing.");
      return;
    }
    setSyncingId(source.id);
    const resp = await triggerVendorInventorySync(source.id);
    if (resp) {
      message.success(
        `Sync enqueued for ${source.name || `source #${source.id}`}`
      );
      // Refresh after a short delay so the new run shows up
      setTimeout(loadSources, 1500);
    }
    setSyncingId(null);
  };

  const columns: ColumnsType<IVendorInventorySource> = [
    {
      title: "Source",
      key: "source",
      render: (_v, row) => (
        <div>
          <div className={styles.sourceName}>
            {row.name || `Source #${row.id}`}
          </div>
          <div className={styles.sourceVendor}>
            {row.vendor_name} · {row.source_type}
          </div>
        </div>
      ),
    },
    {
      title: "Current Items",
      key: "current_inventory_count",
      width: 120,
      render: (_v, row) => (
        <span style={{ fontWeight: 600, fontSize: 13 }}>
          {row.current_inventory_count}
        </span>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: 130,
      render: (_v, row) => <StatusBadge status={row.last_run_status} />,
    },
    {
      title: "Last Run",
      key: "last_run",
      width: 140,
      render: (_v, row) => (
        <Tooltip
          title={
            row.last_run_started_at
              ? dayjs(row.last_run_started_at).format("DD MMM YYYY, hh:mm A")
              : ""
          }
        >
          <span style={{ fontSize: 12, color: "#475569" }}>
            {formatRelativeTime(row.last_run_started_at)}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Frequency",
      key: "frequency",
      width: 110,
      render: (_v, row) => (
        <span style={{ fontSize: 12, color: "#475569" }}>
          every {row.sync_frequency_minutes}m
        </span>
      ),
    },
    {
      title: "Enabled",
      key: "enabled",
      width: 100,
      render: (_v, row) =>
        row.is_enabled ? (
          <Tag color="green">Enabled</Tag>
        ) : (
          <Tag color="default">Disabled</Tag>
        ),
    },
    {
      title: "Action",
      key: "action",
      width: 200,
      render: (_v, row) => (
        <div style={{ display: "flex", gap: 6 }}>
          <Button
            size="small"
            icon={<PlayCircleOutlined />}
            loading={syncingId === row.id}
            disabled={!row.is_enabled}
            onClick={(e) => handleManualSync(e, row)}
            style={{ padding: "0px 16px" }}
          >
            Sync
          </Button>
          <Button
            size="small"
            icon={<FileSearchOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleOpenRuns(row);
            }}
          >
            Runs
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className={styles.tabContainer}>
      {/* ===== Stat tiles ===== */}
      <StatsTiles stats={stats} loading={sourcesLoading} />

      {/* ===== Toolbar ===== */}
      <div className={styles.toolbar}>
        <div className={styles.toolbarTitle}>
          <DatabaseOutlined /> Inventory Sources
        </div>
        <Input.Search
          placeholder="Search by source / vendor name"
          allowClear
          style={{ width: 280 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select
          value={enabledFilter}
          onChange={(v) => {
            setEnabledFilter(v);
            setSourcesPage(1);
          }}
          style={{ width: 140 }}
          options={[
            { value: "all", label: "All sources" },
            { value: "enabled", label: "Enabled" },
            { value: "disabled", label: "Disabled" },
          ]}
        />
        <Button icon={<ReloadOutlined />} onClick={loadSources}>
          Refresh
        </Button>
      </div>

      {/* ===== Sources table ===== */}
      {sourcesLoading && sources.length === 0 ? (
        <div className={styles.centerWrap}>
          <Spin />
        </div>
      ) : filteredSources.length === 0 ? (
        <div className={styles.centerWrap}>
          <InboxOutlined className={styles.emptyIcon} />
          <h3 className={styles.emptyTitle}>No inventory sources</h3>
          <p className={styles.emptySubtitle}>
            Once a VendorInventorySource is created (via the API or admin), it
            will appear here with sync history and one-click manual triggers.
          </p>
        </div>
      ) : (
        <div className={styles.sourcesCard}>
          <Table
            rowKey="id"
            columns={columns}
            dataSource={filteredSources}
            loading={sourcesLoading}
            pagination={{
              current: sourcesPage,
              total: sourcesTotal,
              pageSize: PAGE_SIZE,
              showSizeChanger: false,
              onChange: (p) => setSourcesPage(p),
              showTotal: (t, [from, to]) => `${from}-${to} of ${t}`,
            }}
            onRow={(row) => ({
              className: styles.sourceRow,
              onClick: () => handleOpenRuns(row),
            })}
          />
        </div>
      )}

      {/* ===== Drawers ===== */}
      <RunsDrawer
        open={runsDrawerOpen}
        source={activeSource}
        onClose={() => setRunsDrawerOpen(false)}
        onOpenRun={handleOpenRun}
      />
      <RunDetailDrawer
        open={runDrawerOpen}
        runId={activeRunId}
        onClose={() => setRunDrawerOpen(false)}
      />
    </div>
  );
};

export default InventorySyncTab;
